import { createContext, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormStep from "../../components/forms/FormStep";
import ManulifeFooter from "./components/ManulifeFooter";
import ProfessionalIndemnityFormRoutes from "./Form/routes";
import { eligibilityValidations, limitValidations, termsConditionsValidations } from "../../helpers/professional_indemnity/validations";
import { IndemnityFormState } from "./Form/types";
import createInitialState from "./Form/state";
import { personalDetailsValidations } from "../../helpers/travel/validations";
import useEvent from "../../hooks/useEvent";
import ManulifeHeader from "./ManulifeHeader";
import { QueryClient, QueryClientProvider, useMutation } from "react-query";
import { parseState } from "../../helpers/shared/serialization";
import clientApiService from "../../services/professional_indemnity/clientApiService";
import professionalIndemnity from "./models/professionalIndemnity";
import { useTranslation } from "react-i18next";
import { useStepNavigation } from "../../hooks/useStepNavigation";

type ChangeHandler = <T extends keyof IndemnityFormState>(key: T, value: IndemnityFormState[T]) => void;
type test = (t: IndemnityFormState) => void;

/* istanbul ignore next reason: case that never happens. Testing this would be a bit pointless */
const noop = () => {
  console.log("no-op");
};

export const IndemnityFormContext = createContext<{
  state: IndemnityFormState;
  onChange: ChangeHandler;
  clientId: string;
  isSubmitting: boolean;
  submitError: unknown;
  setFormState: test;
}>({
  onChange: noop,
  state: createInitialState(false),
  clientId: "",
  isSubmitting: false,
  submitError: null,
  setFormState: noop,
});

enum Steps {
  "Eligibility",
  "Limit",
  "PersonalDetails",
  "Summary",
  "Terms",
}

const getBasePath = (isManulifeSv: boolean) => (isManulifeSv ? "/professional_indemnity/manulifesv/form" : "/professional_indemnity/manulife/form");
const getURLForStep = (step: string, isManulifeSv: boolean) => `${getBasePath(isManulifeSv)}/${step}`;

// Tracks the url instead of step since it allows to respond to browser location seamlessly
const stepValidations = [eligibilityValidations, limitValidations, personalDetailsValidations, {}, termsConditionsValidations];
const steps: Steps[] = [Steps.Eligibility, Steps.Limit, Steps.PersonalDetails, Steps.Summary, Steps.Terms];
const amountOfSteps = steps.length;

const getInitialState = (storageKey: any, isManulifeSv: boolean) => {
  const sessionStorageState = sessionStorage.getItem(storageKey);
  try {
    return createInitialState(isManulifeSv, sessionStorageState ? (parseState(sessionStorageState) as IndemnityFormState) : {});
  } catch (e) {
    console.error("Session storage could not be parsed", e);
    return createInitialState(isManulifeSv);
  }
};

const useSubmit = (state: IndemnityFormState, clientId: string) => {
  return useMutation(`${clientId}/professional_indemnity/checkout`, () => clientApiService.checkout(clientId, professionalIndemnity(state)), {
    onSuccess: ({ checkoutUrl }) => {
      window.location.href = checkoutUrl;
    },
  });
};

const NewProductForm: FC = () => {
  const location = useLocation();
  const { t } = useTranslation("pages", { keyPrefix: "professionalIndemnity" });
  const isManulifeSv = location.pathname.includes("manulifesv");
  const storageKey = isManulifeSv ? "professional_indemnity/manulifesv" : "professional_indemnity/manulife";
  const [formState, setFormState] = useState<IndemnityFormState>(getInitialState(storageKey, isManulifeSv));
  const onFieldChange = useCallback<ChangeHandler>((key, value) => setFormState(state => ({ ...state, [key]: value })), []);
  const navigate = useNavigate();
  const stepsURLS = useMemo(
    () => [
      getURLForStep("eligibility", isManulifeSv),
      getURLForStep("limit", isManulifeSv),
      getURLForStep("personal", isManulifeSv),
      getURLForStep("summary", isManulifeSv),
      getURLForStep("terms", isManulifeSv),
    ],
    [isManulifeSv]
  );
  const step = useMemo(() => stepsURLS.indexOf(location.pathname), [location.pathname]);
  const persistState = useEvent(() => sessionStorage.setItem(storageKey, JSON.stringify(formState)));
  useStepNavigation<IndemnityFormState>(step, stepValidations as [], formState, stepsURLS);

  useEffect(() => {
    window.addEventListener("beforeunload", persistState);
    return () => {
      persistState();
      window.removeEventListener("beforeunload", persistState);
    };
  }, [persistState]);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setFormState(prevState => ({ ...prevState, isSVFlow: isManulifeSv }));
  }, [location]);

  const goToNextStep = useCallback(() => {
    const newURL = stepsURLS[step + 1];
    newURL && navigate(newURL);
  }, [step, stepsURLS, navigate]);

  const goToPreviousStep = useCallback(() => {
    const newURL = stepsURLS[step - 1];
    newURL && navigate(newURL);
  }, [step, stepsURLS, navigate]);

  const clientId = isManulifeSv ? "manulifesv" : "manulife";
  const onSubmit = useSubmit(formState, clientId);

  return (
    <IndemnityFormContext.Provider
      value={{
        setFormState: setFormState,
        onChange: onFieldChange,
        state: formState,
        clientId,
        isSubmitting: onSubmit.isLoading,
        submitError: onSubmit.error,
      }}
    >
      <main>
        <form>
          <div className="flex flex-col min-h-screen gap-0">
            <ManulifeHeader hideAccount home={t("header.home")} />
            <FormStep current={step + 1} total={amountOfSteps} />
            <ProfessionalIndemnityFormRoutes
              isNewTranslationFlow={isManulifeSv}
              onPrevious={step > 0 ? goToPreviousStep : null}
              onNext={step === steps.length - 1 ? onSubmit.mutate : goToNextStep}
            />
            <ManulifeFooter />
          </div>
        </form>
      </main>
    </IndemnityFormContext.Provider>
  );
};

const NewProduct: FC = () => {
  const queryClient = useRef(new QueryClient());

  return (
    <QueryClientProvider client={queryClient.current}>
      <NewProductForm />
    </QueryClientProvider>
  );
};

export default NewProduct;
